import React, { useEffect, useState } from "react";
import Header from "@/components/layout/Header";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Check, Sparkles, Star, Crown, Trophy, TrendingUp, Radio, Video, Bell, ShieldCheck } from "lucide-react";

export default function Membership() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [me, setMe] = useState(null);
  const [cycle, setCycle] = useState("monthly");
  const [busy, setBusy] = useState("");

  const loadMe = () => user && api.get("/memberships/me").then(({ data }) => setMe(data)).catch(() => {});
  useEffect(() => {
    api.get("/memberships/plans").then(({ data }) => setData(data)).catch(() => setData(null));
    loadMe();
  }, [user]);

  const activate = (tier) => {
    if (!user) { toast.error("Sign in to choose a membership"); return; }
    setBusy(tier);
    api.post("/memberships/activate", { tier, cycle })
      .then(({ data }) => { setMe(data); toast.success(`You're on the ${tier} plan`); })
      .catch((e) => toast.error(e?.response?.data?.detail || "Failed")).finally(() => setBusy(""));
  };

  const promo = data?.promo;
  const fmt = (n) => (n === 0 ? "Free" : `$${n}`);

  return (
    <div className="min-h-screen bg-[#F1F3F6]"><Header />
      <div className="bg-[#0A192F] text-white">
        <div className="max-w-6xl mx-auto px-6 py-12 text-center">
          <div className="label-overline text-[#E63946]">CBR Memberships</div>
          <h1 className="font-display text-4xl sm:text-5xl uppercase tracking-tighter mt-2">Choose Your Membership</h1>
          {promo?.active && (
            <div data-testid="promo-banner" className="mt-5 inline-flex items-center gap-2 bg-[#E63946]/15 border border-[#E63946]/40 text-white rounded-full px-5 py-2">
              <Sparkles className="w-4 h-4 text-[#E63946]" />
              <span className="text-sm font-semibold">{promo.message || `CBR is FREE for the ${promo.season || "2026 Summer Season"} — join now to become a permanent Founding Member.`}</span>
            </div>
          )}
          <div className="mt-6 inline-flex bg-white/10 rounded-full p-1" data-testid="cycle-toggle">
            {["monthly", "annual"].map((c) => (
              <button key={c} data-testid={`cycle-${c}`} onClick={() => setCycle(c)}
                className={`px-4 py-1.5 text-xs font-semibold uppercase tracking-wider rounded-full ${cycle === c ? "bg-white text-[#0A192F]" : "text-white/60"}`}>{c}{c === "annual" ? " · save" : ""}</button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-10" data-testid="membership-page">
        {!data ? <div className="text-center text-slate-400 py-20">Loading plans…</div> : (
          <div className="grid md:grid-cols-3 lg:grid-cols-5 gap-4 items-start">
            {data.plans.map((p) => {
              const price = p.price[cycle];
              const current = me?.tier === p.key;
              const showFreeLaunch = promo?.active && p.key !== "free" && price > 0;
              return (
                <div key={p.key} data-testid={`plan-${p.key}`}
                  className={`bg-white rounded-lg border p-5 relative ${p.highlight ? "border-[#E63946] shadow-lg" : "border-slate-200"}`}>
                  {p.highlight && <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 text-[10px] font-bold uppercase tracking-wider bg-[#E63946] text-white px-2.5 py-0.5 rounded-full flex items-center gap-1"><Star className="w-3 h-3" /> Most Popular</span>}
                  <div className="text-[11px] uppercase tracking-wider text-slate-400">{p.audience}</div>
                  <div className="font-display text-2xl uppercase tracking-tight mt-0.5">{p.name}</div>
                  <div className="text-xs text-slate-500">{p.tagline}</div>
                  <div className="mt-4">
                    {showFreeLaunch ? (
                      <div><span className="font-data text-3xl text-[#0A192F]">Free</span>
                        <div className="text-[11px] text-slate-400"><span className="line-through">{fmt(price)}/{cycle === "annual" ? "yr" : "mo"}</span> · {promo.label}</div></div>
                    ) : (
                      <div><span className="font-data text-3xl text-[#0A192F]">{fmt(price)}</span>
                        {price > 0 && <span className="text-xs text-slate-400">/{cycle === "annual" ? "yr" : "mo"}</span>}</div>
                    )}
                  </div>
                  <ul className="mt-4 space-y-2 min-h-[160px]">
                    {p.features.map((f, i) => <li key={i} className="flex items-start gap-2 text-[13px] text-slate-600"><Check className="w-3.5 h-3.5 text-[#E63946] mt-0.5 shrink-0" /> {f}</li>)}
                  </ul>
                  <button data-testid={`activate-${p.key}`} disabled={busy === p.key || current} onClick={() => activate(p.key)}
                    className={`mt-4 w-full py-2.5 rounded-sm text-sm font-semibold uppercase tracking-wider ${current ? "bg-emerald-50 text-emerald-700 cursor-default" : p.highlight ? "bg-[#E63946] text-white hover:bg-[#c62f3b]" : "bg-[#0A192F] text-white hover:bg-[#0A192F]/90"} disabled:opacity-60`}>
                    {current ? "Current Plan" : p.key === "free" ? "Get Started" : showFreeLaunch ? "Join Free" : "Choose Plan"}
                  </button>
                </div>
              );
            })}
          </div>
        )}
        {me?.founding_member && (
          <div className="mt-8 flex items-center justify-center gap-2 text-sm text-[#0A192F]" data-testid="founding-member-note">
            <Crown className="w-4 h-4 text-[#E63946]" /> You're a <b>Founding Member</b> — thank you for building CBR early.
          </div>
        )}
        <p className="text-center text-xs text-slate-400 mt-6">All memberships are FREE for the 2026 Summer Season — no card required. Organizations that join now become permanent Founding Members.</p>
      </div>

      <ValueSection />
    </div>
  );
}

const VALUE = [
  { icon: Trophy, title: "Get Recruited", desc: "A full recruiting dashboard, shareable portfolio and QR code that put you in front of college and pro scouts." },
  { icon: TrendingUp, title: "Verified Metrics & Rankings", desc: "The CBR Rating™ and verified performance data coaches actually trust — not self-reported numbers." },
  { icon: Radio, title: "Run Your Program", desc: "CBR Live scoring (Basic & Advanced), unlimited rosters, staff roles and branded team portfolios." },
  { icon: Video, title: "Media & Highlights", desc: "Build a video library with YouTube, Hudl, Vimeo & FieldLevel, and showcase a featured highlight reel." },
  { icon: Bell, title: "Never Miss a Moment", desc: "Live scores, game notifications and a family calendar so parents and fans follow every inning." },
  { icon: ShieldCheck, title: "National Exposure", desc: "Rankings, leaderboards and Trending on CBR surface the best of Canadian baseball to the whole country." },
];

function ValueSection() {
  return (
    <section className="bg-white border-t border-slate-200" data-testid="membership-value-section">
      <div className="max-w-6xl mx-auto px-6 py-16">
        <div className="text-center max-w-2xl mx-auto">
          <div className="label-overline text-[#E63946]">Why Join CBR</div>
          <h2 className="font-display text-3xl sm:text-4xl uppercase tracking-tighter text-[#0A192F] mt-2">What Comes With a CBR Membership</h2>
          <p className="text-sm text-slate-500 mt-3">More than a subscription — CBR is the home of Canadian amateur baseball. Here's what you unlock.</p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-10">
          {VALUE.map((v) => (
            <div key={v.title} data-testid={`value-${v.title.toLowerCase().replace(/[^a-z]/g, "-")}`}
              className="group bg-[#F8F9FA] border border-slate-200 rounded-lg p-6 hover:border-[#E63946] hover:shadow-sm transition-all">
              <div className="w-11 h-11 bg-[#0A192F] rounded-sm grid place-items-center group-hover:bg-[#E63946] transition-colors">
                <v.icon className="w-5 h-5 text-white" />
              </div>
              <h3 className="font-display text-lg uppercase tracking-tight text-[#0A192F] mt-4">{v.title}</h3>
              <p className="text-sm text-slate-600 mt-1.5 leading-relaxed">{v.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
